import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { SimpleSelect } from "../../../../components/ui/select/index.js";
import { cn } from "../../../../lib/utils.js";
import { TechnicalValue } from "../../TechnicalValue.js";
import {
  isTemplateEditorLanguageReadOnly,
  isTemplateEditorNameReadOnly,
  templateEditorMobileOrderClass,
  templateEditorSelectContentClassName,
  templateEditorSelectItemClassName,
  templateEditorSelectTriggerClassName,
} from "../../template-editor-presentation.js";
import { isCategoryEditable } from "../template-management-status.js";
import {
  pruneStaleExamples,
  type TemplateFormState,
} from "../template-management-form.js";
import { TemplateButtonEditor } from "../TemplateButtonEditor/index.js";
import { TemplateLanguageSelect } from "../TemplateLanguageSelect/index.js";
import { TemplateVariableExamples } from "../TemplateVariableExamples/index.js";
import { buildTemplateLanguageOptions } from "../template-language-options.js";
import { findLanguageLabel } from "../template-language-select-state.js";

export type TemplateFormMode = "create" | "edit";

export interface TemplateFormProps {
  mode: TemplateFormMode;
  state: TemplateFormState;
  onChange: (state: TemplateFormState) => void;
  templateStatus?: string;
  disabled?: boolean;
}

const labelClass = "grid gap-1 text-sm font-medium text-ink";
const helpClass = "text-[0.8rem] leading-snug font-normal text-ink-subtle";
const inputClass =
  "w-full min-w-0 rounded-md border border-border-strong bg-surface px-3 py-2 text-sm font-normal text-ink";
const checkboxClass = "flex items-center gap-2 text-sm font-medium text-ink";
const sectionOrder = {
  basic: cn(templateEditorMobileOrderClass.basic, "md:order-none md:col-start-1"),
  header: cn(templateEditorMobileOrderClass.header, "md:order-none md:col-start-1"),
  body: cn(templateEditorMobileOrderClass.body, "md:order-none md:col-start-1"),
  footer: cn(templateEditorMobileOrderClass.footer, "md:order-none md:col-start-1"),
  examples: cn(
    templateEditorMobileOrderClass.examples,
    "empty:hidden md:order-none md:col-start-1",
  ),
  buttons: cn(templateEditorMobileOrderClass.buttons, "md:order-none md:col-start-1"),
};

function FormSection({
  id,
  title,
  description,
  className,
  children,
}: {
  id: string;
  title: string;
  description?: string;
  className: string;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <section
      className={cn("grid min-w-0 gap-3.5 border-b border-line pb-7", className)}
      aria-labelledby={id}
    >
      <div className="grid gap-1">
        <h3 id={id} className="m-0 text-[0.8125rem] font-semibold text-ink">
          {title}
        </h3>
        {description ? <p className={cn("m-0", helpClass)}>{description}</p> : null}
      </div>
      {children}
    </section>
  );
}

function LockedValue({
  label,
  helper,
  children,
}: {
  label: string;
  helper: string;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <div className="grid gap-1">
      <span className="text-sm font-medium text-ink">{label}</span>
      <div className="flex min-h-10 items-center rounded-md border border-line bg-surface-muted px-3 py-2 text-sm text-ink-muted">
        {children}
      </div>
      <span className={helpClass}>{helper}</span>
    </div>
  );
}

export function TemplateForm({
  mode,
  state,
  onChange,
  templateStatus,
  disabled = false,
}: TemplateFormProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const titlePrefix = `template-form-${mode}`;
  const nameReadOnly = isTemplateEditorNameReadOnly(mode);
  const languageReadOnly = isTemplateEditorLanguageReadOnly(mode);

  function patch(partial: Partial<TemplateFormState>): void {
    onChange(pruneStaleExamples({ ...state, ...partial }));
  }

  const categoryEditable =
    mode === "create" || (templateStatus ? isCategoryEditable(templateStatus) : true);

  const languageOptions = useMemo(
    () => buildTemplateLanguageOptions(i18n.language),
    [i18n.language],
  );
  const languageLabel = findLanguageLabel(languageOptions, state.language);

  return (
    <div className="contents">
      <FormSection
        id={`${titlePrefix}-basic`}
        title={t("whatsapp.templates.manage.sections.basic")}
        className={sectionOrder.basic}
      >
        {nameReadOnly ? (
          <LockedValue
            label={t("whatsapp.templates.manage.fields.name")}
            helper={t("whatsapp.templates.manage.fields.nameLocked")}
          >
            <TechnicalValue className="min-w-0 wrap-anywhere">{state.name}</TechnicalValue>
          </LockedValue>
        ) : (
          <label className={labelClass}>
            {t("whatsapp.templates.manage.fields.name")}
            <input
              className={cn(inputClass, "[unicode-bidi:isolate]")}
              dir="ltr"
              value={state.name}
              disabled={disabled}
              onChange={(event) => patch({ name: event.target.value })}
            />
            <span className={helpClass}>{t("whatsapp.templates.manage.fields.nameHelp")}</span>
          </label>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          {languageReadOnly ? (
            <LockedValue
              label={t("whatsapp.templates.manage.language")}
              helper={t("whatsapp.templates.manage.fields.languageLocked")}
            >
              {languageLabel === state.language ? (
                <TechnicalValue className="min-w-0 wrap-anywhere">{state.language}</TechnicalValue>
              ) : (
                <span className="min-w-0">
                  {languageLabel}{" "}
                  <TechnicalValue className="text-ink-subtle">({state.language})</TechnicalValue>
                </span>
              )}
            </LockedValue>
          ) : (
            <div className="grid gap-1">
              <span className="text-sm font-medium text-ink">
                {t("whatsapp.templates.manage.language")}
              </span>
              <TemplateLanguageSelect
                value={state.language}
                onChange={(language) => patch({ language })}
                disabled={disabled}
              />
            </div>
          )}

          {categoryEditable ? (
            <div className="grid gap-1">
              <span className="text-sm font-medium text-ink">
                {t("whatsapp.templates.manage.fields.category")}
              </span>
              <SimpleSelect
                value={state.category}
                onValueChange={(value) =>
                  patch({ category: value as TemplateFormState["category"] })
                }
                ariaLabel={t("whatsapp.templates.manage.fields.category")}
                triggerClassName={templateEditorSelectTriggerClassName}
                itemClassName={templateEditorSelectItemClassName}
                contentClassName={templateEditorSelectContentClassName}
                options={[
                  {
                    value: "UTILITY",
                    label: t("whatsapp.templates.manage.categories.UTILITY"),
                  },
                  {
                    value: "MARKETING",
                    label: t("whatsapp.templates.manage.categories.MARKETING"),
                  },
                ]}
              />
            </div>
          ) : (
            <LockedValue
              label={t("whatsapp.templates.manage.fields.category")}
              helper={t("whatsapp.templates.manage.fields.categoryLocked")}
            >
              {t(`whatsapp.templates.manage.categories.${state.category}`)}
            </LockedValue>
          )}
        </div>

        <div className="grid gap-1 sm:max-w-sm">
          <span className="text-sm font-medium text-ink">
            {t("whatsapp.templates.manage.fields.parameterFormat")}
          </span>
          <SimpleSelect
            value={state.parameterFormat}
            onValueChange={(value) =>
              patch({ parameterFormat: value as TemplateFormState["parameterFormat"] })
            }
            ariaLabel={t("whatsapp.templates.manage.fields.parameterFormat")}
            triggerClassName={templateEditorSelectTriggerClassName}
            itemClassName={templateEditorSelectItemClassName}
            contentClassName={templateEditorSelectContentClassName}
            options={[
              {
                value: "POSITIONAL",
                label: t("whatsapp.templates.manage.parameterFormat.POSITIONAL"),
              },
              {
                value: "NAMED",
                label: t("whatsapp.templates.manage.parameterFormat.NAMED"),
              },
            ]}
          />
        </div>
      </FormSection>

      <FormSection
        id={`${titlePrefix}-header`}
        title={t("whatsapp.templates.manage.sections.header")}
        className={sectionOrder.header}
      >
        <label className={checkboxClass}>
          <input
            type="checkbox"
            className="size-4 accent-[var(--color-accent)]"
            checked={state.hasHeader}
            disabled={disabled}
            onChange={(event) => patch({ hasHeader: event.target.checked })}
          />
          {t("whatsapp.templates.manage.fields.includeHeader")}
        </label>
        {state.hasHeader ? (
          <label className={labelClass}>
            {t("whatsapp.templates.manage.fields.headerText")}
            <input
              className={inputClass}
              value={state.headerText}
              maxLength={60}
              disabled={disabled}
              onChange={(event) => patch({ headerText: event.target.value })}
            />
          </label>
        ) : null}
      </FormSection>

      <FormSection
        id={`${titlePrefix}-body`}
        title={t("whatsapp.templates.manage.sections.body")}
        description={t("whatsapp.templates.manage.fields.bodyHint")}
        className={sectionOrder.body}
      >
        <label className={labelClass}>
          {t("whatsapp.templates.manage.fields.bodyText")}
          <textarea
            className={cn(inputClass, "min-h-[9rem] resize-y leading-[1.5]")}
            value={state.bodyText}
            maxLength={1024}
            rows={7}
            disabled={disabled}
            onChange={(event) => patch({ bodyText: event.target.value })}
          />
        </label>
      </FormSection>

      <FormSection
        id={`${titlePrefix}-footer`}
        title={t("whatsapp.templates.manage.sections.footer")}
        className={sectionOrder.footer}
      >
        <label className={checkboxClass}>
          <input
            type="checkbox"
            className="size-4 accent-[var(--color-accent)]"
            checked={state.hasFooter}
            disabled={disabled}
            onChange={(event) => patch({ hasFooter: event.target.checked })}
          />
          {t("whatsapp.templates.manage.fields.includeFooter")}
        </label>
        {state.hasFooter ? (
          <label className={labelClass}>
            {t("whatsapp.templates.manage.fields.footerText")}
            <input
              className={inputClass}
              value={state.footerText}
              maxLength={60}
              disabled={disabled}
              onChange={(event) => patch({ footerText: event.target.value })}
            />
          </label>
        ) : null}
      </FormSection>

      <div
        className={cn(
          sectionOrder.examples,
          "min-w-0 [&:not(:empty)]:border-b [&:not(:empty)]:border-line [&:not(:empty)]:pb-7",
        )}
      >
        <TemplateVariableExamples state={state} onChange={onChange} disabled={disabled} />
      </div>

      <FormSection
        id={`${titlePrefix}-buttons`}
        title={t("whatsapp.templates.manage.sections.buttons")}
        className={cn(sectionOrder.buttons, "border-b md:border-b-0 md:pb-0")}
      >
        <TemplateButtonEditor
          buttons={state.buttons}
          onChange={(buttons) => patch({ buttons })}
          disabled={disabled}
        />
      </FormSection>
    </div>
  );
}
